import {
    faArrowDown,
    faArrowUp,
    faCheckCircle,
    faCheckDouble,
    faClock,
    faCreditCard,
    faExchangeAlt,
    faFingerprint,
    faMapMarkerAlt,
    faMoneyBillWave,
    faPassport
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import PropTypes from 'prop-types';
import React from 'react';
import { Badge, Card, Col, OverlayTrigger, Row, Tooltip } from 'react-bootstrap';

/**
 * Componente para mostrar una factura recibida individual como tarjeta
 */
const FacturaRecibidaCard = ({ factura, isSelected = false, onToggleSelection }) => {
    const url = `/contabilidad/facturacion/facturas-recibidas/visualizacion/${factura.idFacturaRecibida}`;
    const isInactive = factura.bIsActive === false;

    const formattedDate = new Date(factura.dFechaEmision).toLocaleDateString("es-MX", {
        day: "2-digit",
        month: "short",
        year: "numeric"
    }).replace(/(?:^|\s)([a-z])/g, char => char.toUpperCase());

    const currency = factura.vMoneda === 'USD' ? 'USD' : 'MXN';
    const isMonedaExtranjera = factura.vMoneda !== 'MXN';
    const tipoCambio = parseFloat(factura.dTipoCambio) || 1;

    const getTipoComprobanteConfig = (tipo) => {
        const configs = {
            I: { label: 'Ingreso', variant: 'primary', icon: faArrowDown },
            E: { label: 'Egreso', variant: 'danger', icon: faArrowUp },
            T: { label: 'Traslado', variant: 'info', icon: faExchangeAlt },
            P: { label: 'Pago', variant: 'success', icon: faMoneyBillWave }
        };
        return configs[tipo] || { label: 'Desconocido', variant: 'secondary', icon: faMoneyBillWave };
    };

    const tipoConfig = getTipoComprobanteConfig(factura.vTipoComprobante);
    const isEgreso = factura.vTipoComprobante === 'E';

    const total = isEgreso ? -Math.abs(factura.dTotal) : factura.dTotal;
    const subtotal = isEgreso ? -Math.abs(factura.dSubTotal) : factura.dSubTotal;

    // Calcular equivalente en MXN
    const totalMXN = total * tipoCambio;
    const handleCardClick = (e) => {
        if (e.target.tagName === 'A' || e.target.closest('a') || e.target.closest('button')) {
            return;
        }

        if (onToggleSelection) {
            onToggleSelection(factura.idFacturaRecibida);
        }
    };

    // Determinar clases CSS seg�n estado
    const cardClasses = [
        'mb-3',
        'shadow-sm',
        isInactive ? 'border-danger' : '',
        isSelected ? 'border-primary border-3' : ''
    ].filter(Boolean).join(' ');

    // Estilos din�micos para el fondo compatible con modo oscuro
    const cardStyle = {
        cursor: 'pointer',
        transition: 'all 0.2s ease-in-out',
        ...(isSelected && {
            backgroundColor: 'rgba(13, 110, 253, 0.1)',
            boxShadow: '0 0 0 0.2rem rgba(13, 110, 253, 0.25)'
        })
    };

    return (
        <Card
            className={cardClasses}
            onClick={handleCardClick}
            style={cardStyle}
        >
            <Card.Body>
                <Row className="align-items-center">
                    {/* Serie, Folio y Bot�n Ver */}
                    <Col xs={12} md={2} className="mb-2 mb-md-0">
                        <div className="d-flex flex-column">
                            <div className="d-flex align-items-center gap-2 mb-1">
                                {isSelected && (
                                    <FontAwesomeIcon
                                        icon={faCheckCircle}
                                        className="text-primary"
                                        size="lg"
                                    />
                                )}
                                <span className={`fw-bold text-wrap ${isInactive ? 'text-danger' : ''}`} style={{ fontSize: '1rem' }}>
                                    {factura.vSerie}<br></br>{factura.vFolio}
                                </span>
                                {isInactive && (
                                    <OverlayTrigger
                                        placement="top"
                                        overlay={
                                            <Tooltip>
                                                Esta factura no est� siendo toma en cuenta en el comprobante
                                            </Tooltip>
                                        }
                                    >
                                        <div
                                            style={{
                                                width: 10,
                                                height: 10,
                                                borderRadius: '50%',
                                                backgroundColor: 'red',
                                                display: 'inline-block'
                                            }}
                                        ></div>
                                    </OverlayTrigger>
                                )}
                            </div>
                            <small className={`mb-2 ${isInactive ? 'text-danger' : 'text-muted'}`}>
                                {formattedDate}
                            </small>

                            {/* Badge de registro como gasto */}
                            {factura.bIsRegistradoComoGasto !== undefined && (
                                <div className="mt-2">
                                    <OverlayTrigger
                                        placement="top"
                                        overlay={
                                            <Tooltip>
                                                {factura.bIsRegistradoComoGasto
                                                    ? 'Esta factura ya fue registrada como gasto'
                                                    : 'Esta factura a�n no ha sido registrada como gasto'}
                                            </Tooltip>
                                        }
                                    >
                                        <Badge
                                            bg={factura.bIsRegistradoComoGasto ? 'success' : 'warning'}
                                            className="fs--2 d-flex align-items-center gap-1"
                                        >
                                            <FontAwesomeIcon
                                                icon={factura.bIsRegistradoComoGasto ? faCheckDouble : faClock}
                                                size="sm"
                                            />
                                            {factura.bIsRegistradoComoGasto ? 'Registrado' : 'Pendiente'}
                                        </Badge>
                                    </OverlayTrigger>
                                </div>
                            )}
                        </div>
                    </Col>

                    {/* Informaci�n principal del emisor */}
                    <Col xs={12} md={6} className="mb-2 mb-md-0">
                        <div className="mb-2">
                            <h6 className={`mb-1 ${isInactive ? 'text-danger' : ''}`}>
                                <span className="text-truncate d-inline-block">
                                    {factura.vRfcEmisor !== 'XEXX010101000' &&
                                        <img src="/img/bandera-mexico-48.png" width="20" alt="M�xico" className="me-1"></img>
                                    } {factura.vRazonSocialEmisor}
                                </span>
                            </h6>
                            <div className={`small mb-1 ${isInactive ? 'text-danger' : 'text-muted'}`}>
                                <strong>RFC:</strong> {factura.vRfcEmisor}
                            </div>
                            {factura.vRegimenFiscalReceptor && (
                                <div className={`small mb-1 ${isInactive ? 'text-danger' : 'text-muted'}`}>
                                    <strong>R&eacute;gimen Fiscal:</strong> {factura.vRegimenFiscalReceptor}
                                </div>
                            )}
                            {/* Uso CFDI */}
                            {factura.vUsoCfdi && (
                                <div className={`small mb-2 ${isInactive ? 'text-danger' : 'text-muted'}`}>
                                    <strong>Uso CFDI:</strong>{' '}
                                    <span className="d-inline-block">
                                        {factura.vUsoCfdi}
                                    </span>
                                </div>
                            )}

                            {factura.NumRegIdTrib && (
                                <div className={`small mb-1 ${isInactive ? 'text-danger' : 'text-muted'}`}>
                                    <FontAwesomeIcon icon={faPassport} className="me-1 text-info" />
                                    <strong>Num. Reg. Id. Trib.:</strong> {factura.NumRegIdTrib}
                                </div>
                            )}
                            {factura.vResidenciaFiscal && (
                                <div className={`small mb-2 ${isInactive ? 'text-danger' : 'text-muted'}`}>
                                    <FontAwesomeIcon icon={faMapMarkerAlt} className="me-1 text-warning" />
                                    <strong>Residencia Fiscal:</strong> {factura.vResidenciaFiscal}
                                </div>
                            )}
                        </div>

                        {/* Badges informativos */}
                        <div className="d-flex flex-wrap gap-2 align-items-center mb-2">
                            <Badge bg={tipoConfig.variant} className="d-flex align-items-center gap-1">
                                <FontAwesomeIcon icon={tipoConfig.icon} />
                                {tipoConfig.label}
                            </Badge>

                            <Badge bg="light" text="dark" className="fs--2">
                                {factura.vMetodoPago || 'N/A'}
                            </Badge>

                            {factura.vFormaPago && (
                                <Badge bg="light" text="dark" className="fs--2">
                                    <FontAwesomeIcon icon={faCreditCard} className="me-1" />
                                    {factura.vFormaPago}
                                </Badge>
                            )}
                        </div>

                        {/* UUID */}
                        {factura.vUUIDFactura && (
                            <OverlayTrigger
                                placement="top"
                                overlay={<Tooltip>Click para ver factura - UUID: {factura.vUUIDFactura}</Tooltip>}
                            >
                                <div className={`small mb-2 ${isInactive ? 'text-danger' : 'text-muted'}`}>
                                    <FontAwesomeIcon icon={faFingerprint} className="me-1" />
                                    <strong>UUID:</strong>{' '}
                                    <a
                                        href={url}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="text-decoration-none d-inline-block"
                                        style={{ cursor: 'pointer' }}
                                    >
                                        <span className="d-inline-block text-primary" style={{ textDecoration: 'underline' }}>
                                            {factura.vUUIDFactura}
                                        </span>
                                    </a>
                                </div>
                            </OverlayTrigger>
                        )}
                    </Col>

                    {/* Informaci�n financiera */}
                    <Col xs={12} md={4} className="text-md-end">
                        <div className="mb-2">
                            <h5 className={`mb-1 ${isInactive ? 'text-danger' : isEgreso ? 'text-danger' : 'text-success'}`}>
                                {total.toLocaleString("es-MX", {
                                    style: "currency",
                                    currency: currency,
                                    minimumFractionDigits: 2,
                                    maximumFractionDigits: 2
                                })}
                            </h5>

                            <div className="d-flex justify-content-md-end align-items-center gap-2 flex-wrap">
                                <Badge
                                    bg={factura.vMoneda === 'MXN' ? 'success' : 'warning'}
                                    className="fs--2"
                                >
                                    {factura.vMoneda}
                                </Badge>
                                {tipoCambio !== 1 && (
                                    <small className="text-muted">
                                        T.C. {tipoCambio.toFixed(4)}
                                    </small>
                                )}
                            </div>
                        </div>

                        {/* Desglose de montos */}
                        <div className={`small ${isInactive ? 'text-danger' : 'text-muted'}`}>
                            <div className="d-flex justify-content-md-end gap-3 mb-1">
                                <span>
                                    <strong>SUBTOTAL:</strong>{' '}
                                    {subtotal.toLocaleString("es-MX", {
                                        style: "currency",
                                        currency: currency
                                    })}
                                </span>
                            </div>
                            <div className="d-flex justify-content-md-end gap-3">
                                <span>
                                    <strong>IMP. TRASL:</strong>{' '}
                                    {(isEgreso
                                        ? -Math.abs(factura.dTotalImpuestosTrasladados)
                                        : factura.dTotalImpuestosTrasladados
                                    ).toLocaleString("es-MX", {
                                        style: "currency",
                                        currency: currency
                                    })}
                                </span>
                                {factura.dTotalImpuestosRetenidos > 0 && (
                                    <span>
                                        <strong>IMP. RET:</strong>{' '}
                                        {parseFloat(factura.dTotalImpuestosRetenidos).toLocaleString("es-MX", {
                                            style: "currency",
                                            currency: currency
                                        })}
                                    </span>
                                )}
                            </div>
                            {factura.dDescuento !== undefined && factura.dDescuento !== null && factura.dDescuento > 0 && (
                                <div className="d-flex justify-content-md-end gap-3 mt-2">
                                    <span>
                                        <strong>DESCUENTO:</strong>{' '}
                                        <span className="text-danger">-{parseFloat(factura.dDescuento).toLocaleString("es-MX", {
                                            style: "currency",
                                            currency: currency
                                        })}</span>
                                    </span>
                                </div>
                            )}
                        </div>


                        {/* Equivalente en MXN para monedas extranjeras */}
                        {isMonedaExtranjera && (
                            <>
                                <hr></hr>
                                <div >
                                    <strong className="small">MXN</strong>{' '}
                                    <b className={`${isInactive ? 'text-danger' : 'text-primary'} mb-1`}>{totalMXN.toLocaleString("es-MX", {
                                        style: "currency",
                                        currency: "MXN",
                                        minimumFractionDigits: 2,
                                        maximumFractionDigits: 2
                                    })} </b>
                                </div>
                            </>

                        )}
                    </Col>
                </Row>
            </Card.Body>
        </Card>
    );
};

FacturaRecibidaCard.propTypes = {
    factura: PropTypes.shape({
        idFacturaRecibida: PropTypes.number.isRequired,
        vSerie: PropTypes.string,
        vFolio: PropTypes.string.isRequired,
        dFechaEmision: PropTypes.string.isRequired,
        vRazonSocialEmisor: PropTypes.string.isRequired,
        vRfcEmisor: PropTypes.string.isRequired,
        vRegimenFiscalReceptor: PropTypes.string,
        NumRegIdTrib: PropTypes.string,
        vResidenciaFiscal: PropTypes.string,
        vTipoComprobante: PropTypes.string.isRequired,
        vMetodoPago: PropTypes.string,
        vFormaPago: PropTypes.string,
        vUsoCfdi: PropTypes.string,
        vUUIDFactura: PropTypes.string,
        vMoneda: PropTypes.string.isRequired,
        dTipoCambio: PropTypes.number,
        dSubTotal: PropTypes.number.isRequired,
        dTotal: PropTypes.number.isRequired,
        dTotalImpuestosTrasladados: PropTypes.number,
        dTotalImpuestosRetenidos: PropTypes.number,
        dDescuento: PropTypes.number,
        bIsActive: PropTypes.bool,
        bIsRegistradoComoGasto: PropTypes.bool
    }).isRequired,
    isSelected: PropTypes.bool,
    onToggleSelection: PropTypes.func
};

export default FacturaRecibidaCard;
